import type { ScheduleTemplate } from "@/pages/human-resources/time-management/business/schedule-template/ScheduleTemplate.ts";
import type { DailyTag } from "@/pages/human-resources/time-management/business/daily-tag/DailyTag.ts";
import {
  cleanWhiteSpaces,
  normalizedCharacters,
} from "@/shared/utils/Strings.ts";

export type EntityScope = "system" | "domain" | "site";

export function scopeForTemplate(st: ScheduleTemplate): EntityScope {
  return st.siteId === undefined ? "domain" : "site";
}

export function scopeForDailyTag(dt: DailyTag): EntityScope {
  if (dt.siteId !== undefined) return "site";
  if (dt.domainId !== undefined) return "domain";
  return "system";
}

interface DuplicatesScopes<T> {
  entities: Set<T>;
  scopes: Set<EntityScope>;
}

export function findTemplatesDuplicateIdsAndScopes(
  templates: ScheduleTemplate[],
): DuplicatesScopes<ScheduleTemplate> {
  return findDuplicateEntities(templates, scopeForTemplate);
}

export function findDailyTagsDuplicateIdsAndScopes(
  dailyTags: DailyTag[],
): DuplicatesScopes<DailyTag> {
  return findDuplicateEntities(dailyTags, scopeForDailyTag);
}

function findDuplicateEntities<T extends DailyTag | ScheduleTemplate>(
  entities: T[],
  scopeDetector: (e: T) => EntityScope,
): DuplicatesScopes<T> {
  const duplicates = new Set<T>();
  const scopes = new Set<EntityScope>();
  const groupByCleanLabels = new Map<string, T[]>();
  entities.forEach((tpl) => {
    const cleanLabel = normalizedCharacters(cleanWhiteSpaces(tpl.label));
    const group = groupByCleanLabels.get(cleanLabel) ?? [];
    group.push(tpl);
    groupByCleanLabels.set(cleanLabel, group);
  });
  groupByCleanLabels.forEach((group) => {
    if (group.length > 1) {
      group.forEach((ts) => {
        duplicates.add(ts);
        scopes.add(scopeDetector(ts));
      });
    }
  });
  return { entities: duplicates, scopes };
}
